import { fetchSignatureAssetBase64 } from "../helpers/signature-assets-api";

/** So werden Attachment-Bilder gespeichert: cid:sunnysign-<32 Hex>.<ext> */
const CID_ASSET_SRC = /^cid:(sunnysign-([0-9a-f]{32})\.(?:png|jpe?g|gif|webp))$/i;

const IMG_TAG = /<img\b[^>]*>/gi;
const CID_SRC_IN_TAG = /(\bsrc\s*=\s*)(["'])cid:(sunnysign-([0-9a-f]{32})\.(?:png|jpe?g|gif|webp))\2/i;

/** 1×1 transparentes GIF, bis der echte Blob-Inhalt geladen ist. */
export const ASSET_PLACEHOLDER_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

type ResolvedAsset = { dataUrl: string; fileName: string; contentType: string };

const assetCache = new Map<string, Promise<ResolvedAsset | null>>();

export function hexToGuid(hex32: string): string {
  return [
    hex32.slice(0, 8),
    hex32.slice(8, 12),
    hex32.slice(12, 16),
    hex32.slice(16, 20),
    hex32.slice(20),
  ].join("-");
}

/** Stellt die Marker aus dem cid:-src wieder her (Signaturen, die vor dem Attribut-Fix gespeichert wurden). */
function recoverAssetMarkers(img: HTMLImageElement): void {
  if (img.getAttribute("data-sunnysign-asset")?.trim()) return;
  const match = CID_ASSET_SRC.exec(img.getAttribute("src")?.trim() ?? "");
  if (!match) return;
  img.setAttribute("data-sunnysign-asset", hexToGuid(match[2].toLowerCase()));
  img.setAttribute("data-sunnysign-filename", match[1]);
}

/** Marker aller Attachment-Bilder unterhalb von root wiederherstellen. */
export function recoverAssetMarkersIn(root: ParentNode): void {
  for (const img of Array.from(root.querySelectorAll("img")) as HTMLImageElement[]) {
    recoverAssetMarkers(img);
  }
}

/**
 * Tauscht cid:-Quellen gegen den Platzhalter, bevor das HTML in den Live-DOM kommt — sonst startet
 * der Browser einen Ladeversuch, den die CSP der Taskpane blockt. Reine String-Ersetzung, damit
 * dabei nicht schon wieder geparst wird; resolveAssetImagesForDisplay setzt danach den Blob-Inhalt.
 */
export function prepareAssetImagesForDisplay(html: string): string {
  if (!html) return html || "";
  return html.replace(IMG_TAG, (tag) => {
    const match = CID_SRC_IN_TAG.exec(tag);
    if (!match) return tag;
    const withPlaceholder = tag.replace(
      CID_SRC_IN_TAG,
      (_full, prefix: string, quote: string) => `${prefix}${quote}${ASSET_PLACEHOLDER_SRC}${quote}`
    );
    if (/\bdata-sunnysign-asset\s*=/i.test(withPlaceholder)) return withPlaceholder;
    const assetId = hexToGuid(match[4].toLowerCase());
    return withPlaceholder.replace(
      /^<img\b/i,
      `<img data-sunnysign-asset="${assetId}" data-sunnysign-filename="${match[3]}"`
    );
  });
}

function loadAsset(assetId: string): Promise<ResolvedAsset | null> {
  const cached = assetCache.get(assetId);
  if (cached) return cached;
  const pending = fetchSignatureAssetBase64(assetId)
    .then((asset) => {
      const contentType = asset.contentType || "image/png";
      return {
        dataUrl: `data:${contentType};base64,${asset.base64}`,
        fileName: asset.fileName,
        contentType,
      };
    })
    .catch((e) => {
      console.warn("[SunnySign] Attachment-Bild konnte nicht geladen werden:", assetId, e);
      assetCache.delete(assetId);
      return null;
    });
  assetCache.set(assetId, pending);
  return pending;
}

/**
 * Macht Attachment-Bilder anzeigbar: cid: ist in der Taskpane per CSP blockiert, also wird der
 * Blob-Inhalt als data:-URL eingesetzt. Die data-sunnysign-*-Marker bleiben erhalten, damit beim
 * Speichern und beim Einfügen in die Mail wieder cid: entsteht.
 */
export async function resolveAssetImagesForDisplay(root: ParentNode | null | undefined): Promise<void> {
  if (!root || typeof document === "undefined") return;

  recoverAssetMarkersIn(root);

  const byAsset = new Map<string, HTMLImageElement[]>();
  for (const img of Array.from(root.querySelectorAll("img")) as HTMLImageElement[]) {
    const assetId = img.getAttribute("data-sunnysign-asset")?.trim();
    if (!assetId) continue;
    const src = img.getAttribute("src")?.trim() ?? "";
    // blob: (gerade eingefügt) und data: (schon aufgelöst) sind bereits darstellbar.
    if (src && src !== ASSET_PLACEHOLDER_SRC && !/^cid:/i.test(src)) continue;
    const targets = byAsset.get(assetId);
    if (targets) targets.push(img);
    else byAsset.set(assetId, [img]);
  }
  if (byAsset.size === 0) return;

  await Promise.all(
    Array.from(byAsset, async ([assetId, targets]) => {
      const asset = await loadAsset(assetId);
      if (!asset) return;
      for (const img of targets) {
        img.setAttribute("src", asset.dataUrl);
        if (asset.fileName) img.setAttribute("data-sunnysign-filename", asset.fileName);
        img.setAttribute("data-sunnysign-ctype", asset.contentType);
      }
    })
  );
}
